/**
 * Pure helpers for QuotationForm save payload (no inventory / Profit / order discount).
 */

import type { Client, SalesPerson } from '../../api/clients';
import type { OrderFormLine, OrderFormTaxConfig, OrderTotals } from '../orders/orderForm/types';
import {
  buildOrderProductsPayload,
  normalizeCreditFields,
  resolveCreatorSalesPersonString,
  validateOrderFormForSave,
} from '../orders/orderForm/orderFormPayload';
import type { QuoteCreate, QuoteUpdate } from '../../models/quote';

export { validateOrderFormForSave, resolveCreatorSalesPersonString };

export function buildQuotePayload(input: {
  selectedClient: Client;
  shippingAddress: string;
  creatorSalesPerson: string;
  paymentMethod: string;
  paymentCurrency: string;
  creditEnabled: boolean;
  creditTermsDays: string;
  creditDueDate: string;
  totals: OrderTotals;
  exchangeRate: number;
  comment: string;
  selectedProducts: OrderFormLine[];
  applyIva: boolean;
  taxConfig: OrderFormTaxConfig;
}): QuoteCreate {
  const { creditTermsDays, dueDate } = normalizeCreditFields({
    creditEnabled: input.creditEnabled,
    creditTermsDays: input.creditTermsDays,
    creditDueDate: input.creditDueDate,
  });

  const products = buildOrderProductsPayload(input.selectedProducts, {
    applyIva: input.applyIva,
    taxConfig: input.taxConfig,
    showSaleProfitPlusFields: false,
  });

  return {
    clientId: input.selectedClient.id!,
    clientName: input.selectedClient.businessName || '',
    creatorSalesPerson: input.creatorSalesPerson,
    paymentMethod: input.paymentMethod,
    paymentCurrency: input.paymentCurrency,
    creditTermsDays,
    dueDate,
    subtotal: input.totals.subtotal,
    ivaAmount: input.totals.ivaAmount,
    total: input.totals.total,
    exchangeRate: input.exchangeRate,
    comment: input.comment || '',
    shippingAddress: input.shippingAddress || '',
    products,
  };
}

export function toQuoteUpdate(create: QuoteCreate): QuoteUpdate {
  return {
    clientId: create.clientId,
    clientName: create.clientName,
    creatorSalesPerson: create.creatorSalesPerson,
    paymentMethod: create.paymentMethod,
    paymentCurrency: create.paymentCurrency,
    creditTermsDays: create.creditTermsDays,
    dueDate: create.dueDate,
    subtotal: create.subtotal,
    ivaAmount: create.ivaAmount,
    total: create.total,
    exchangeRate: create.exchangeRate,
    comment: create.comment,
    shippingAddress: create.shippingAddress,
    products: create.products,
  };
}

export type { SalesPerson };
